// Phone validation utility for country-specific format validation

// Country-specific phone number rules
const PHONE_VALIDATION_RULES = {
  1: {
    // US & Canada
    minDigits: 10,
    maxDigits: 10,
    pattern: /^\+1[2-9]\d{2}[2-9]\d{6}$/,
    name: "United States/Canada",
  },
  44: {
    // United Kingdom
    minDigits: 10,
    maxDigits: 10,
    pattern: /^\+44[1-9]\d{8,9}$/,
    name: "United Kingdom",
  },
  49: {
    // Germany
    minDigits: 10,
    maxDigits: 12,
    pattern: /^\+49[1-9]\d{9,11}$/,
    name: "Germany",
  },
  33: {
    // France
    minDigits: 9,
    maxDigits: 9,
    pattern: /^\+33[1-9]\d{8}$/,
    name: "France",
  },
  39: {
    // Italy
    minDigits: 9,
    maxDigits: 10,
    pattern: /^\+39[0-9]\d{8,9}$/,
    name: "Italy",
  },
  34: {
    // Spain
    minDigits: 9,
    maxDigits: 9,
    pattern: /^\+34[6-9]\d{8}$/,
    name: "Spain",
  },
  61: {
    // Australia
    minDigits: 9,
    maxDigits: 9,
    pattern: /^\+61[2-9]\d{8}$/,
    name: "Australia",
  },
  81: {
    // Japan
    minDigits: 10,
    maxDigits: 11,
    pattern: /^\+81[1-9]\d{8,9}$/,
    name: "Japan",
  },
  86: {
    // China
    minDigits: 11,
    maxDigits: 11,
    pattern: /^\+86[1-9]\d{10}$/,
    name: "China",
  },
  91: {
    // India
    minDigits: 10,
    maxDigits: 10,
    pattern: /^\+91[6-9]\d{9}$/,
    name: "India",
  },
};

/**
 * Validates phone number format based on country code
 * @param {string} phoneNumber - Phone number in E.164 format (e.g., "+1234567890")
 * @returns {object} - Validation result with isValid boolean and error message
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || !phoneNumber.startsWith("+")) {
    return {
      isValid: false,
      error: "Phone number must be in international format starting with +",
    };
  }

  // Extract country code
  let countryCode = null;
  let rule = null;

  // Try to match country codes (check longer codes first)
  const sortedCodes = Object.keys(PHONE_VALIDATION_RULES).sort(
    (a, b) => b.length - a.length
  );

  for (const code of sortedCodes) {
    if (phoneNumber.startsWith(`+${code}`)) {
      countryCode = code;
      rule = PHONE_VALIDATION_RULES[code];
      break;
    }
  }

  if (!rule) {
    // Basic E.164 validation for unsupported countries
    const basicPattern = /^\+[1-9]\d{1,14}$/;
    if (basicPattern.test(phoneNumber)) {
      return {
        isValid: true,
        error: null,
      };
    } else {
      return {
        isValid: false,
        error: "Invalid phone number format",
      };
    }
  }

  // Get the number part (without country code and +)
  const numberPart = phoneNumber.substring(countryCode.length + 1);
  const digitCount = numberPart.length;

  // Check digit count
  if (digitCount < rule.minDigits) {
    return {
      isValid: false,
      error: `${rule.name} phone numbers must have at least ${rule.minDigits} digits`,
    };
  }

  if (digitCount > rule.maxDigits) {
    return {
      isValid: false,
      error: `${rule.name} phone numbers cannot exceed ${rule.maxDigits} digits`,
    };
  }

  // Check pattern
  if (!rule.pattern.test(phoneNumber)) {
    return {
      isValid: false,
      error: `Invalid ${rule.name} phone number format`,
    };
  }

  return {
    isValid: true,
    error: null,
  };
};

/**
 * Get expected digit count for a country code
 * @param {string} countryCode - Country code (e.g., "1", "44")
 * @returns {object} - Object with minDigits, maxDigits, and country name
 */
export const getPhoneRequirements = (countryCode) => {
  const rule = PHONE_VALIDATION_RULES[countryCode];
  if (rule) {
    return {
      minDigits: rule.minDigits,
      maxDigits: rule.maxDigits,
      countryName: rule.name,
    };
  }

  // Default for unsupported countries
  return {
    minDigits: 7,
    maxDigits: 14,
    countryName: "International",
  };
};

/**
 * Extract country code from E.164 phone number
 * @param {string} phoneNumber - Phone number in E.164 format
 * @returns {string|null} - Country code or null if not found
 */
export const extractCountryCode = (phoneNumber) => {
  if (!phoneNumber || !phoneNumber.startsWith("+")) {
    return null;
  }

  const sortedCodes = Object.keys(PHONE_VALIDATION_RULES).sort(
    (a, b) => b.length - a.length
  );

  for (const code of sortedCodes) {
    if (phoneNumber.startsWith(`+${code}`)) {
      return code;
    }
  }

  return null;
};
